export type User = {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  locale: string | null;
};

export type StoredFile = {
  id: string;
  kind: "signature" | "pdf" | "word" | "export";
  name: string;
  mimeType: string;
  size: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export async function getSession(): Promise<User | null> {
  const response = await fetch("/api/session", { credentials: "include" });
  if (!response.ok) return null;
  const data = (await response.json()) as { user: User | null };
  return data.user;
}

export function startGoogleSignIn(returnTo = window.location.pathname): void {
  const url = new URL("/api/auth/google/start", window.location.origin);
  if (returnTo.startsWith("/") && !returnTo.startsWith("//") && !returnTo.startsWith("/api/")) {
    url.searchParams.set("return_to", returnTo);
  }
  window.location.href = `${url.pathname}${url.search}`;
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
  });
}

export async function listFiles(kind?: StoredFile["kind"]): Promise<StoredFile[]> {
  const url = new URL("/api/files", window.location.origin);
  if (kind) url.searchParams.set("kind", kind);
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) return [];
  const data = (await response.json()) as { files: StoredFile[] };
  return data.files;
}

export async function uploadStoredFile(input: {
  file: Blob;
  kind: StoredFile["kind"];
  name: string;
  metadata?: Record<string, unknown>;
}): Promise<StoredFile> {
  const form = new FormData();
  form.set("file", input.file, input.name);
  form.set("kind", input.kind);
  form.set("name", input.name);
  form.set("metadata", JSON.stringify(input.metadata ?? {}));

  const response = await fetch("/api/files", {
    method: "POST",
    body: form,
    credentials: "include",
  });

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(errorBody.error ?? "Upload failed");
  }

  const data = (await response.json()) as { file: StoredFile };
  return data.file;
}

export async function deleteStoredFile(id: string): Promise<void> {
  const response = await fetch(`/api/files/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error("Delete failed");
}

export async function fetchStoredFileBlob(file: StoredFile): Promise<Blob> {
  const response = await fetch(`/api/files/${file.id}/download`, { credentials: "include" });
  if (!response.ok) throw new Error("Download failed");
  return response.blob();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
